import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Key, Shield, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import NavBar from "../components/NavBar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { useAuth } from '../contexts/AuthContext';
import config from '../config/config';
import { setAccessToken } from '../services/authService';
import { validateEmail } from "../utils/validation";

const Login = () => {
  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Add validation error state
  const [validationErrors, setValidationErrors] = useState({
    email: "",
    password: "",
  });
  
  // Add password visibility state
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email before submission
    const isEmailValid = validateEmail(formData.email);
    if (!isEmailValid) {
      setValidationErrors(prev => ({
        ...prev,
        email: "Please enter a valid email address"
      }));
      toast.error("Please enter a valid email address");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await login(formData.email, formData.password);
      
      // Explicitly log the response for debugging
      console.log('Login response:', response);
      
      // IMPORTANT: Check for access token in response body
      const tokenFromResponse = response?.accessToken;
      if (tokenFromResponse) {
        console.log('Setting access token from login response body');
        setAccessToken(tokenFromResponse);
      } else {
        console.warn('No token found in response');
      }
      
      // Add a small delay to ensure state updates
      setTimeout(() => {
        toast.success("Login successful!");
        // Check if the user was redirected from another page
        const from = location.state?.from;
        // If from path starts with /profile, it means they were trying to view a profile
        if (from && from.startsWith('/profile')) {
          navigate(from);
        } else {
          // Redirect to feed (home page) by default or to the page they were trying to access
          navigate(from && from !== '/' ? from : '/');
        }
      }, 100);
    } catch (error) {
      console.error('Login error:', error);
      toast.error("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Save the intended destination in localStorage
    // If the user was redirected to login from another page, save that path
    const from = location.state?.from;
    // Add special handling for profile pages
    if (from && from.startsWith('/profile')) {
      console.log('Redirecting to profile page after login:', from);
    }
    localStorage.setItem('redirect_after_login', from || '/');
    
    // Execute Google sign-in logic
    signInWithGoogle();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate email on change
    if (name === 'email') {
      const isValid = validateEmail(value);
      setValidationErrors(prev => ({
        ...prev,
        email: isValid ? "" : "Please enter a valid email address"
      }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <NavBar />
      
      <div className="container mx-auto px-4 pt-20">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome back
            </h1>
          </div>

          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-3">
              <Key className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <strong className="block text-gray-900">Access Your Account</strong>
                Sign in to manage your wills and see matched offers.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <strong className="block text-gray-900">Secure Access</strong>
                Your data is protected with industry-standard encryption.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <strong className="block text-gray-900">Privacy First</strong>
                We never share your personal information with third parties.
              </p>
            </div>
          </div>

          <Button
            onClick={handleGoogleLogin}
            className="w-full mb-6 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4 mr-2" />
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <Separator className="my-4" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="bg-white px-2 text-gray-500 text-sm">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={validationErrors.email ? "border-red-500" : ""}
              />
              {validationErrors.email && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-0 h-full px-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading || !!validationErrors.email}
              className="w-full bg-black text-white hover:bg-black/90"
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <Link to="/signin" className="text-blue-600 hover:text-blue-700">
                Create account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
